import { getFirebaseAuth } from '@/core/firebase/firebase.config';
import { GoogleAuthProvider, OAuthProvider, signInWithPopup, signOut, getIdToken } from 'firebase/auth';
import { ISocialAuthCredential, ISocialAuthUser } from '../../domain/interfaces/social-auth.interface';
import { logger } from '@/core/utils/logger';

export class FirebaseAuthDataSource {
  /**
   * Inicia sesión con Google utilizando Firebase Authentication
   * @returns Credenciales del usuario de Google
   */
  async signInWithGoogle(): Promise<ISocialAuthCredential> {
    try {
      // Crear un proveedor de autenticación de Google
      const provider = new GoogleAuthProvider();

      // Configurar los scopes que necesitamos
      provider.addScope('email');
      provider.addScope('profile');

      // Configurar el flujo de autenticación para dispositivos móviles
      provider.setCustomParameters({
        prompt: 'select_account',
      });

      // Realizar el inicio de sesión con popup
      const result = await signInWithPopup(getFirebaseAuth(), provider);

      // Obtener las credenciales de Google
      const credential = GoogleAuthProvider.credentialFromResult(result);

      if (!credential) {
        throw new Error('No se pudieron obtener las credenciales de Google');
      }

      // Obtener el ID token (que es lo que necesita Firebase Admin en el backend)
      const idToken = await getIdToken(result.user);

      // Obtener el token de acceso
      const accessToken = credential.accessToken || '';

      // Obtener la información del usuario
      const user: ISocialAuthUser = {
        uid: result.user.uid,
        email: result.user.email || '',
        displayName: result.user.displayName || '',
        photoURL: result.user.photoURL || '',
      };

      return {
        user,
        accessToken,
        idToken,
        providerId: credential.providerId,
      };
    } catch (error: unknown) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error al iniciar sesión con Google:', normalizedError, { module: 'auth', action: 'firebaseGoogleSignIn' });
      throw error;
    }
  }

  /**
   * Inicia sesión con Microsoft (Azure AD) utilizando Firebase Authentication.
   * El token Firebase resultante se envia al backend como `socialToken` con
   * `network=MICROSOFT` y el backend verifica que `firebase.sign_in_provider`
   * sea `microsoft.com` via Firebase Admin SDK.
   *
   * @param tenantId Opcional: tenant de Azure AD para SSO corporativo.
   *                 Si no se especifica, usa `common` (acepta cualquier tenant).
   */
  async signInWithMicrosoft(tenantId?: string): Promise<ISocialAuthCredential> {
    try {
      const provider = new OAuthProvider('microsoft.com');

      provider.addScope('email');
      provider.addScope('profile');
      provider.addScope('openid');

      const customParams: Record<string, string> = {
        prompt: 'select_account',
      };
      if (tenantId) {
        customParams.tenant = tenantId;
      }
      provider.setCustomParameters(customParams);

      const result = await signInWithPopup(getFirebaseAuth(), provider);

      const credential = OAuthProvider.credentialFromResult(result);
      if (!credential) {
        throw new Error('No se pudieron obtener las credenciales de Microsoft');
      }

      // Para login Microsoft via Firebase, enviamos el ID token de Firebase
      // (que incluye `firebase.sign_in_provider='microsoft.com'`) al backend.
      const idToken = await getIdToken(result.user);
      const accessToken = credential.accessToken || '';

      const user: ISocialAuthUser = {
        uid: result.user.uid,
        email: result.user.email || '',
        displayName: result.user.displayName || '',
        photoURL: result.user.photoURL || '',
      };

      return {
        user,
        accessToken,
        idToken,
        providerId: credential.providerId,
      };
    } catch (error: unknown) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error al iniciar sesión con Microsoft:', normalizedError, {
        module: 'auth',
        action: 'firebaseMicrosoftSignIn',
      });
      throw error;
    }
  }

  /**
   * Cierra la sesión en Firebase
   */
  async signOut(): Promise<void> {
    try {
      await signOut(getFirebaseAuth());
    } catch (error: unknown) {
      const normalizedError = error instanceof Error ? error : new Error(String(error));
      logger.error('Error al cerrar sesión en Firebase:', normalizedError, { module: 'auth', action: 'firebaseSignOut' });
      throw error;
    }
  }
}
